import { IconicCheck2, IconicClose, IconicShieldWarning } from '@insify/design-system/iconic-icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import * as styles from './StatusBadge.module.css';

import type { CoverageStatus } from '../../types/Policy';

const Badge = ({
  contentKey,
  icon,
  style,
  ns,
}: {
  contentKey: string;
  icon: JSX.Element;
  style?: string;
  ns: 'policy' | 'invoice';
}) => {
  const { t } = useTranslation([ns]);

  return (
    <div className={styles.policyInfo}>
      <div className={clsx(styles.paymentStatus, style)}>
        {icon}
        <span data-testid='coverage-status'>{t(contentKey)}</span>
      </div>
    </div>
  );
};

const Policy = ({ status, className = '' }: { status: CoverageStatus; className?: string }) => {
  let icon: JSX.Element;
  let style: string;
  let contentKey: string;

  switch (status) {
    case 'insured':
      icon = <IconicCheck2 />;
      style = clsx(styles.active, className);
      contentKey = 'policy:coverage_payment_status_insured';
      break;
    case 'insuredWithPaymentIssues':
      icon = <IconicShieldWarning />;
      style = clsx(styles.pending, className);
      contentKey = 'policy:coverage_payment_status_insured_with_payment_issue';
      break;
    case 'cancelled':
      icon = <IconicClose />;
      style = clsx(styles.inactive, className);
      contentKey = 'policy:coverage_payment_status_cancelled';
      break;
  }

  return <Badge ns='policy' icon={icon} style={style} contentKey={contentKey} />;
};

/**
 * TODO: create cases for different invoice statuses
 * @returns invoice status badge
 */
const Invoice = ({ isPaid, className = '' }: { isPaid: boolean; className?: string }) => {
  let icon: JSX.Element;
  let style: string;
  let contentKey: string;

  if (isPaid) {
    icon = <IconicCheck2 height={20} width={20} />;
    style = clsx(styles.active, className);
    contentKey = 'invoice:paid';
  } else {
    icon = <IconicClose />;
    style = clsx(styles.inactive, className);
    contentKey = 'invoice:unpaid';
  }

  return <Badge ns='invoice' icon={icon} style={style} contentKey={contentKey} />;
};

export const StatusBadge = {
  Policy,
  Invoice,
};
